<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Register business</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Register your business</h1>
        <p class="page-head__subtitle">
          Enter your company details to create your CapX Flow business profile.
        </p>
      </div>
    </div>
  </header>

  <mat-card class="entity-card" appearance="outlined">
    <mat-card-content>
      <p class="step-label">Business Registration</p>

      @if (serverError()) {
        <div class="inline-error" role="alert" aria-live="assertive">
          {{ serverError() }}
        </div>
      }

      <form class="entity-form" [formGroup]="form" novalidate (ngSubmit)="onSubmit()">
        <!-- Business name -->
        <div class="field-group">
          <label class="field-label" for="entityName">
            Business Name <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityName"
              type="text"
              formControlName="name"
              placeholder="e.g. Acme Pty Ltd"
              autocomplete="organization"
            />
            <mat-error>
              @if (form.controls.name.hasError('required')) {
                Business name is required
              } @else if (form.controls.name.hasError('maxlength')) {
                Name must be 255 characters or fewer
              } @else if (form.controls.name.hasError('serverError')) {
                {{ form.controls.name.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <!-- Trading name (optional) -->
        <div class="field-group">
          <label class="field-label" for="entityTradingName">Trading Name</label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityTradingName"
              type="text"
              formControlName="tradingName"
              placeholder="Defaults to business name"
            />
            <mat-hint>Defaults to business name; update if different</mat-hint>
            <mat-error>
              @if (form.controls.tradingName.hasError('maxlength')) {
                Trading name must be 255 characters or fewer
              } @else if (form.controls.tradingName.hasError('serverError')) {
                {{ form.controls.tradingName.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <!-- Business type -->
        <div class="field-group">
          <label class="field-label" for="entityType" id="entityTypeLabel">
            Business Type <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <mat-select
              id="entityType"
              formControlName="type"
              placeholder="Select a business type"
              aria-labelledby="entityTypeLabel"
            >
              @for (opt of ENTITY_TYPES; track opt.value) {
                <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
              }
            </mat-select>
            <mat-error>
              @if (form.controls.type.hasError('required')) {
                Please select a business type
              } @else if (form.controls.type.hasError('serverError')) {
                {{ form.controls.type.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <!-- Registration number -->
        <div class="field-group">
          <label class="field-label" for="entityRegNo">
            Registration Number <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityRegNo"
              type="text"
              formControlName="regNo"
              placeholder="e.g. 2023/000001/07"
              (blur)="onRegNoBlur()"
            />
            @if (regNoStatus() === 'checking') {
              <span matSuffix class="reg-no-spinner">
                <mat-spinner diameter="16" />
              </span>
            }
            <mat-error>
              @if (form.controls.regNo.hasError('required')) {
                Registration number is required
              } @else if (form.controls.regNo.hasError('regNoFormat')) {
                Use only numbers and forward slashes (/)
              } @else if (form.controls.regNo.hasError('taken')) {
                This business is already registered on CapX. If you believe this is in error, please
                contact
                <a href="mailto:support@flow.capx.co.za">support&#64;flow.capx.co.za</a>.
              } @else if (form.controls.regNo.hasError('serverError')) {
                {{ form.controls.regNo.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
          @if (regNoStatus() === 'available') {
            <p class="field-available" role="status" aria-live="polite">
              Registration number is available
            </p>
          }
          @if (nameToConfirm(); as name) {
            <app-company-name-check [name]="name" (answered)="answerNameCheck($event)" />
          }
        </div>

        <!-- Industry -->
        <div class="field-group">
          <label class="field-label" for="entityIndustry">
            Industry <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              id="entityIndustry"
              type="text"
              formControlName="industry"
              placeholder="e.g. Retail, Construction, Technology"
            />
            <mat-error>
              @if (form.controls.industry.hasError('required')) {
                Industry is required
              } @else if (form.controls.industry.hasError('maxlength')) {
                Industry must be 255 characters or fewer
              } @else if (form.controls.industry.hasError('serverError')) {
                {{ form.controls.industry.getError('serverError') }}
              }
            </mat-error>
          </mat-form-field>
        </div>

        <!-- Trading start date -->
        <div class="field-group">
          <p class="field-label" id="tradingStartLabel">
            Trading Start Date <span class="required-mark" aria-hidden="true">*</span>
          </p>
          <div class="trading-start-row">
            <mat-form-field appearance="outline" class="trading-start-row__month">
              <mat-select
                formControlName="tradingStartMonth"
                placeholder="Month"
                aria-labelledby="tradingStartLabel"
              >
                @for (m of MONTHS; track m.value) {
                  <mat-option [value]="m.value">{{ m.label }}</mat-option>
                }
              </mat-select>
              <mat-error>
                @if (form.controls.tradingStartMonth.hasError('required')) {
                  Required
                } @else if (form.controls.tradingStartMonth.hasError('tradingStartInFuture')) {
                  Trading start date cannot be in the future
                } @else if (form.controls.tradingStartMonth.hasError('serverError')) {
                  {{ form.controls.tradingStartMonth.getError('serverError') }}
                }
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="trading-start-row__year">
              <input
                matInput
                id="tradingStartYear"
                type="number"
                formControlName="tradingStartYear"
                placeholder="Year"
                min="1900"
                aria-labelledby="tradingStartLabel"
              />
              <mat-error>
                @if (form.controls.tradingStartYear.hasError('required')) {
                  Required
                } @else if (form.controls.tradingStartYear.hasError('min')) {
                  Year must be 1900 or later
                } @else if (form.controls.tradingStartYear.hasError('max')) {
                  Year cannot be in the future
                } @else if (form.controls.tradingStartYear.hasError('serverError')) {
                  {{ form.controls.tradingStartYear.getError('serverError') }}
                }
              </mat-error>
            </mat-form-field>
          </div>
        </div>

        <!-- Preparer attestation -->
        <!-- consent_text_version: 2026-11-01. Legal's words, final (DOC-75 §2.4) -->
        <div class="consent-section">
          <h2 class="consent-section__heading">Authority and consent</h2>
          <mat-checkbox formControlName="consent" (change)="onConsentChange($event.checked)">
            I confirm that I am authorised to act for this business. On its behalf, I consent to
            CapX processing the business's information and carrying out credit and compliance checks
            on it, including with credit bureaux, to assess this application and, if it is funded,
            to monitor it. A credit check on any individual — a director, or anyone giving security
            for the business — needs that person's own consent, which CapX will ask them for
            separately.
          </mat-checkbox>
          <p class="consent-section__text">
            CapX uses the information collected here to assess this application, to manage any
            funding that follows, and to meet its legal obligations, in line with the Protection of
            Personal Information Act. CapX will carry out a credit check on an individual only once
            that person has given their own consent.
          </p>
          @if (form.controls.consent.touched && form.controls.consent.hasError('requiredTrue')) {
            <p class="consent-error" role="alert">
              Tick the box to give this consent and continue.
            </p>
          }
        </div>

        <button
          mat-flat-button
          type="submit"
          class="full-width"
          data-testid="create-entity-submit"
          [disabled]="isSubmitDisabled"
          [attr.aria-busy]="isLoading() || null"
        >
          @if (isLoading()) {
            <mat-spinner diameter="20" aria-hidden="true" />
            <span>Registering…</span>
          } @else {
            Register Business
          }
        </button>
      </form>
    </mat-card-content>
  </mat-card>
</div>
